import { formatBs } from "@/components/ui/currency-input";
import { computeGrossPriceBreakdown } from "@/lib/client-price";
import {
  EMPTY_SERVICE_BASELINE,
  serviceSaveBreakdown,
  type ServiceSavedBaseline,
} from "@/lib/service-price-save";
import type { PricingConfigState } from "@/hooks/usePricingConfig";

interface ClientPriceCalculatorProps {
  /**
   * Smjer "baseToGross": `amount` je cijena usluge bez PDV-a koju terapeut upisuje;
   * kalkulator od nje odbija proviziju (ostatak je terapeutov) i dodaje PDV da dobije
   * bruto cijenu za klijenta (forma usluge, onboarding, hitni termini).
   * Smjer "grossToNet": `amount` je već bruto cijena za klijenta, kalkulator je
   * razlaže nadolje na osnovicu, proviziju, PDV i iznos terapeuta (grupna terapija),
   * isti smjer kao `computeSessionMoney`, bez ponovnog izvođenja unazad.
   */
  direction?: "baseToGross" | "grossToNet";
  amount: number | null;
  pricing: PricingConfigState;
  currency?: string;
  /**
   * Samo za "baseToGross": cijena s kojom je forma učitana (bez PDV-a + sirovi
   * spremljeni bruto). Netaknuta cijena se prikazuje iz spremljenog bruta, isto kao što
   * je forma i sprema, pa "Klijent plaća" nikad ne odstupa od spremljenog iznosa.
   */
  baseline?: ServiceSavedBaseline;
}

export function ClientPriceCalculator({
  direction = "baseToGross",
  amount,
  pricing,
  currency = "KM",
  baseline = EMPTY_SERVICE_BASELINE,
}: ClientPriceCalculatorProps) {
  const config = pricing.config;

  if (!config) {
    return (
      <div className="rounded-xl border border-[#e2ded4] bg-[#fbfaf7] p-4">
        <h3
          className="text-sm font-semibold text-foreground"
          style={{ fontFamily: "var(--font-heading)" }}
        >
          Kalkulator cijene
        </h3>
        <p className="mt-1 text-xs text-muted-foreground">
          {pricing.isError
            ? "Trenutno nije moguće učitati konfiguraciju cijena. Osvježite stranicu i pokušajte ponovo."
            : "Cijene se učitavaju."}
        </p>
      </div>
    );
  }

  const breakdown =
    direction === "baseToGross"
      ? serviceSaveBreakdown(amount, baseline, config)
      : computeGrossPriceBreakdown(amount, config);

  const helperText =
    direction === "baseToGross"
      ? "Upišite cijenu usluge bez PDV-a. Platforma od nje zadržava proviziju, a klijentu se dodaje PDV."
      : "Uneseni iznos je cijena za klijenta sa PDV-om. Ispod je prikaz raspodjele na proviziju, PDV i iznos koji vama ostaje.";

  return (
    <div className="rounded-xl border border-[#e2ded4] bg-[#fbfaf7] p-4">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h3
            className="text-sm font-semibold text-foreground"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            Kalkulator cijene
          </h3>
          <p className="mt-1 text-xs text-muted-foreground">{helperText}</p>
        </div>
        <div className="rounded-lg bg-primary/10 px-3 py-2 text-right">
          <span className="block text-[11px] font-medium text-primary">Klijent plaća</span>
          <span className="text-base font-bold tabular-nums text-primary">
            {formatCurrency(breakdown.clientPrice, currency)}
          </span>
        </div>
      </div>

      <div className="mt-4 rounded-lg border border-[#e2ded4] bg-white px-3">
        {/* Cijena → minus provizija → vama ostaje. */}
        <div className="divide-y divide-[#e2ded4]">
          <CalculatorRow label="Cijena usluge (bez PDV-a)" value={breakdown.basePrice} currency={currency} strong />
          <CalculatorRow
            label="Provizija platforme"
            note={`${config.feePercent}%`}
            value={breakdown.platformFee}
            sign="minus"
            currency={currency}
          />
          <CalculatorRow label="Vama ostaje" value={breakdown.therapistAmount} currency={currency} strong />
        </div>
        {/* Odvojeno: cijena + PDV → klijent plaća. */}
        <div className="divide-y divide-[#e2ded4] border-t-2 border-[#e2ded4]">
          <CalculatorRow
            label="PDV"
            note={`${round2(config.vatRate * 100)}%`}
            value={breakdown.vat}
            sign="plus"
            currency={currency}
          />
          <CalculatorRow label="Klijent plaća" value={breakdown.clientPrice} currency={currency} strong />
        </div>
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        Ako radite po ugovoru o djelu, od iznosa koji vam ostaje se pri isplati obustavljaju porez i doprinosi.
      </p>
    </div>
  );
}

function CalculatorRow({
  label,
  note,
  value,
  currency,
  sign,
  strong = false,
}: {
  label: string;
  note?: string;
  value: number;
  currency: string;
  /** Stavka koja se odbija od cijene ili dodaje na nju; uvučena da se čita kao međukorak. */
  sign?: "minus" | "plus";
  strong?: boolean;
}) {
  const prefix = sign && value > 0 ? (sign === "minus" ? "−" : "+") : "";
  return (
    <div className={`flex items-center justify-between gap-4 py-2.5 text-sm ${sign ? "pl-3" : ""}`}>
      <span className={strong ? "font-semibold text-primary" : "text-foreground"}>
        {label}
        {note && <span className="ml-1 text-xs font-normal text-muted-foreground">{note}</span>}
      </span>
      <span className={strong ? "font-bold tabular-nums text-primary" : "font-semibold tabular-nums text-muted-foreground"}>
        {`${prefix}${formatCurrency(value, currency)}`}
      </span>
    </div>
  );
}

function formatCurrency(value: number, currency: string): string {
  return `${formatBs(Math.round(value * 100) / 100)} ${currency}`;
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}
